import { inject, injectable } from 'inversify-props';
import { IHttpClient } from './HttpClient';
import { ILogger } from './LoggerService';
import { ISettings } from './SettingsService';
import { INotification } from './NotificationService';

export interface IExportService {

	exportToExcel(fileName: string, data: any[]): void;
	exportToCsv(fileName: string, data: any[]): void;
	exportToJson(fileName: string, data: any[]): void;

}

@injectable()
export class ExportService implements IExportService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings,
		@inject('INotification') private notification: INotification) { }


	public exportToExcel(fileName: string, data: any[]): void {
		if (data === null || data.length === 0) {
			const message = 'Export to XLS failed because the data provided is null or empty';
			this.logger.error(`[EXPORT] ${message}`);
			this.notification.error(message);
			return;
		}

		let html = '<table border="1px" style="font-size:20px"><tr>';
		const keys = Object.keys(data[0]);
		for (const key of keys) {
			html += `<th>${key}</th>`;
		}
		html += '</tr>';

		for (const row of data) {
			html += '<tr>';
			for (const col of keys) {
				html += `<td>${row[col]}</td>`;
			}
			html += '</tr>';
		}
		html += '</table>';
		const result = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${fileName}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>${html}</table></body></html>`;

		const blob = new Blob([result], { type: 'application/vnd.ms-excel;charset=utf-8' });
		if (navigator.msSaveBlob) {
			navigator.msSaveBlob(blob, fileName);
		} else {
			const link = document.createElement('a');
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute('download', fileName);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}
	}

	public exportToCsv(fileName: string, data: any[]): void {
		if (data === null || data.length === 0) {
			const message = 'Export to CSV failed because the data provided is null or empty';
			this.logger.error(`[EXPORT] ${message}`);
			this.notification.error(message);
			return;
		}
		this.logger.log(`[EXPORT] Exporting ${data.length} records to CSV`);
		const separator = ',';
		const keys = Object.keys(data[0]);
		const result = keys.join(separator) + '\n' + data.map((row) => {
			return keys.map((key) => {
				let cell = row[key] === null || row[key] === undefined ? '' : row[key];
				cell = cell instanceof Date
					? cell.toLocaleString()
					: cell.toString().replace(/"/g, '""');

				if (cell.search(/("|,|\n)/g) >= 0) {
					cell = `"${cell}"`;
				}
				return cell;
			}).join(separator);
		}).join('\n');

		const blob = new Blob([result], { type: 'text/csv;charset=utf-8' });
		if (navigator.msSaveBlob) {
			navigator.msSaveBlob(blob, fileName);
		} else {
			const link = document.createElement('a');
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute('download', fileName);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}
	}

	public exportToJson(fileName: string, data: any[]): void {
		if (data === null || data.length === 0) {
			const message = 'Export to JSON failed because the data provided is null or empty';
			this.logger.error(`[EXPORT] ${message}`);
			this.notification.error(message);
			return;
		}
		this.logger.log(`[EXPORT] Exporting ${data.length} records to CSV`);
		const result = JSON.stringify(data);

		const blob = new Blob([result], { type: 'text/json;charset=utf-8' });
		if (navigator.msSaveBlob) {
			navigator.msSaveBlob(blob, fileName);
		} else {
			const link = document.createElement('a');
			if (link.download !== undefined) {
				const url = URL.createObjectURL(blob);
				link.setAttribute('href', url);
				link.setAttribute('download', fileName);
				link.style.visibility = 'hidden';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}

	}

}
